import { apiRequest, SUPABASE_URL } from "./apiUtils";
import type {
  CMSContent,
  CMSContentTranslation,
  CMSLanguage,
  CMSContentVariable,
} from "@/models/CMS";

export const cmsApi = {
  // Content
  getContent: (id: number) =>
    apiRequest<CMSContent>(`${SUPABASE_URL}/cms-content/${id}`),

  createContent: (data: Partial<CMSContent>) =>
    apiRequest<CMSContent>(`${SUPABASE_URL}/cms-content`, {
      method: "POST",
      body: data,
    }),

  updateContent: (id: number, data: Partial<CMSContent>) =>
    apiRequest<CMSContent>(`${SUPABASE_URL}/cms-content/${id}`, {
      method: "PATCH",
      body: data,
    }),

  updateContentPosition: (id: number, position: number) =>
    apiRequest<CMSContent>(`${SUPABASE_URL}/cms-content/${id}`, {
      method: "PATCH",
      body: { position },
    }),

  deleteContent: (id: number) =>
    apiRequest<{ success: boolean; id: number }>(
      `${SUPABASE_URL}/cms-content/${id}`,
      {
        method: "DELETE",
      }
    ),

  // Translations
  getContentTranslations: (contentId: number) =>
    apiRequest<CMSContentTranslation[]>(
      `${SUPABASE_URL}/cms-content/${contentId}/translations`
    ),

  upsertTranslation: (
    contentId: number,
    languageCode: string,
    data: Partial<CMSContentTranslation>
  ) => {
    console.log(
      `Upserting translation for content ${contentId}, language ${languageCode}`,
      data
    );
    return apiRequest<CMSContentTranslation>(
      `${SUPABASE_URL}/cms-content/${contentId}/translations/${languageCode}`,
      {
        method: "PUT", // This will be handled by the edge function for both create and update
        body: data,
      }
    );
  },

  // Update content name after translation changes (for tree view)
  // This will maintain the original created_at value to preserve ordering
  updateContentName: (contentId: number, name: string) =>
    apiRequest<CMSContent>(`${SUPABASE_URL}/cms-content/${contentId}`, {
      method: "PATCH",
      body: { name },
    }),

  // Languages
  getLanguages: () =>
    apiRequest<CMSLanguage[]>(`${SUPABASE_URL}/cms-languages`),

  // Variables
  getContentVariables: (contentId: number) =>
    apiRequest<CMSContentVariable[]>(
      `${SUPABASE_URL}/cms-content/${contentId}/variables`
    ),

  upsertVariable: (contentId: number, name: string, value: string) =>
    apiRequest<CMSContentVariable>(
      `${SUPABASE_URL}/cms-content/${contentId}/variables/${name}`,
      {
        method: "PUT",
        body: { default_value: value },
      }
    ),

  // Get content tree with hierarchical structure support
  getContentTree: () =>
    apiRequest<CMSContent[]>(`${SUPABASE_URL}/cms-content/tree`),

  // Add method to create navigation structure
  createNavigationItem: (data: Partial<CMSContent>) =>
    apiRequest<CMSContent>(`${SUPABASE_URL}/cms-content/navigation`, {
      method: "POST",
      body: data,
    }),

  // Create a template with automatic structure
  createTemplateFolder: (name: string, parentId?: number) =>
    apiRequest<CMSContent>(`${SUPABASE_URL}/cms-content`, {
      method: "POST",
      body: {
        name,
        type: "navigation",
        parent_id: parentId || null,
        show_in_menu: false,
      },
    }),

  // Get content by slug
  getContentBySlug: (slug: string, languageCode: string) => {
    const params = new URLSearchParams({
      slug,
      languageCode,
    });
    return apiRequest(
      `${SUPABASE_URL}/cms-content/by-slug?${params.toString()}`
    );
  },

  // Get template folders - navigation items with "Template" in their name
  getTemplateFolders: async (): Promise<CMSContent[]> => {
    const content = await apiRequest<CMSContent[]>(
      `${SUPABASE_URL}/cms-content/tree`
    );
    return content.filter(
      (item) =>
        item.type === "navigation" &&
        !item.parent_id &&
        content.some(
          (child) => child.parent_id === item.id && child.type === "template"
        )
    );
  },
};
